import type { DailyRoutineDetails } from '../dto/DailyRoutineDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import type { EmotionalProtocolLogRepository } from '../ports/EmotionalProtocolLogRepository';
import type { IdGenerator } from '../ports/IdGenerator';
import type { LocalDateProvider } from '../ports/LocalDateProvider';
import type { RoutinePersistenceTransactions } from '../ports/RoutinePersistenceTransactions';
import type { RoutineStepTemplateRepository } from '../ports/RoutineStepTemplateRepository';
import type { RoutineTemplateRepository } from '../ports/RoutineTemplateRepository';
import { calculateDailyProgress } from '../../domain/progression/CalculateDailyProgress';
import { applyModeVariant, selectActiveSteps } from '../../domain/routine/DayModeRules';
import { DayMode, TaskStatus } from '../../domain/routine/enums';
import type { DailyRoutine, DailyStep } from '../../domain/routine/models';
import { dayOfWeek } from '../../domain/shared/LogicalDate';
import type { DailyRoutineId, DailyStepId, ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';
import { loadRoutineDetails } from './routineDetails';

export class GetOrCreateTodayRoutine {
  public constructor(
    private readonly dailyRoutines: DailyRoutineRepository,
    private readonly dailySteps: DailyStepRepository,
    private readonly routineTemplates: RoutineTemplateRepository,
    private readonly routineStepTemplates: RoutineStepTemplateRepository,
    private readonly transactions: RoutinePersistenceTransactions,
    private readonly clock: Clock,
    private readonly dates: LocalDateProvider,
    private readonly ids: IdGenerator,
    private readonly emotionalProtocolLogs: EmotionalProtocolLogRepository,
  ) {}

  public async execute(profileId: ProfileId): Promise<Result<DailyRoutineDetails, RoutineError>> {
    try {
      const logicalDate = this.dates.today();
      const existing = await this.dailyRoutines.findByLogicalDate(profileId, logicalDate);
      if (existing)
        return success(
          await loadRoutineDetails(existing, this.dailySteps, this.emotionalProtocolLogs),
        );

      const templates = await this.routineTemplates.listByProfile(profileId);
      const template = templates.find((candidate) => candidate.active);
      if (!template) {
        return failure(
          new RoutineError('active_template_not_found', 'Aucun modèle actif n’est disponible.'),
        );
      }

      const timestamp = this.clock.now().toISOString();
      const routine: DailyRoutine = {
        id: this.ids.generate<DailyRoutineId>(),
        profileId,
        logicalDate,
        templateId: template.id,
        dayMode: DayMode.normal,
        status: 'open',
        completionPercentage: 0,
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      const weekday = dayOfWeek(logicalDate);
      const templateSteps = (await this.routineStepTemplates.listByTemplate(template.id)).filter(
        (step) =>
          step.active &&
          step.daysOfWeek.includes(weekday) &&
          (!step.modes || step.modes.includes(routine.dayMode)),
      );
      const steps = templateSteps.map<DailyStep>((step) =>
        applyModeVariant(
          {
            id: this.ids.generate<DailyStepId>(),
            profileId,
            dailyRoutineId: routine.id,
            templateStepId: step.id,
            title: step.title,
            category: step.category,
            status: TaskStatus.pending,
            order: step.order,
            required: step.required,
            estimatedDurationMinutes: step.estimatedDurationMinutes,
            createdAt: timestamp,
            updatedAt: timestamp,
            ...(step.essential ? { essential: step.essential } : {}),
            ...(step.modes ? { modes: step.modes } : {}),
            ...(step.suggestedTime ? { suggestedTime: step.suggestedTime } : {}),
            data: step.content,
          },
          routine.dayMode,
        ),
      );

      const created = await this.transactions.createRoutineWithStepsIfAbsent(routine, steps);
      if (!created) {
        const concurrent = await this.dailyRoutines.findByLogicalDate(profileId, logicalDate);
        if (!concurrent)
          throw new RoutineError('routine_creation_failed', 'La journée n’a pas pu être créée.');
        return success(
          await loadRoutineDetails(concurrent, this.dailySteps, this.emotionalProtocolLogs),
        );
      }
      return success({
        routine,
        steps,
        progress: calculateDailyProgress(selectActiveSteps(steps, routine.dayMode)),
        emotionalState: null,
      });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
